/**
 * M3 — TARP v5.10 (rota única `/tarp`).
 * -------------------------------------
 * Entrada fiel ao protótipo: painel **Acionamentos manuais** (5 cards diretos,
 * componente reutilizável `@/components/tarp`) → formulário único (barragem +
 * checklist guiado). GUPM-178 Fase B: a abertura nasce como rascunho persistente
 * "Em abertura" (autosave/retomar/descartar) e só vira ABERTA ao confirmar.
 * Persistência auditável via datasource (sem `supabase.from()` na UI), broadcast
 * `tarp:critical`, gating de perfil (operador não encerra).
 */
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AcionamentosManuais, QUICK_ACTIONS, type QuickAction } from '@/components/tarp';
import { BannerArea } from '@/components/BannerArea';
import { useAuth } from '@/hooks/useAuth';
import { useDamScope } from '@/hooks/useDamScope';
import { canClose } from '@/domain/tarp/status';
import { isTarpCode } from '@/domain/tarp/catalog';
import { baixaAuthErrorMessage, isDevAuthBypass, mensagemSemFluxo, NE_TO_TARP_LEVEL, readAtalhoSemFluxo, readAutoOpen, readEmergencyIntent, readManualAction, readOpenTarpId, validateBaixa } from '@/domain';
import { acoesValidas, draftToAcaoPlano, resumoPlano, resumoPlanoExistente } from '@/domain/planos/acoes';
import { openPlanoNavState } from '@/domain/planos/navegacao';
import type { BaixaInput, TarpCode } from '@/domain';
import { useDataSources, type TarpRecord } from '@/services/datasources';
import { AbrirTarpFlow } from './components/AbrirTarpFlow';
import AvisoSemFluxo from './components/AvisoSemFluxo';
import type { AbrirTarpPreselect } from './components/AbrirTarpModal';
import { TarpDetailModal } from './components/TarpDetailModal';
import { TarpList } from './components/TarpList';
import { TarpsFechadasPanel } from './components/TarpsFechadasPanel';
import { quickActionForCode } from './catalog-stub';
import { useActiveDams, useClosedTarps, useCurrentAppUser, useOpenTarps, usePlanosByTarp, useTarpAutoria, useTarpMutations } from './hooks';

function errMsg(e: unknown): string | null {
  if (!e) return null;
  return e instanceof Error ? e.message : String(e);
}

export default function M3Tarp() {
  const { data: tarps = [], isLoading, error: listError } = useOpenTarps();
  const { data: dams = [] } = useActiveDams();
  const { data: currentUser } = useCurrentAppUser();
  const { data: fechadas = [] } = useClosedTarps();
  const { dams: damScope } = useDamScope();
  const { conclude, close } = useTarpMutations();
  // GUPM-290: rótulo do atalho do card por perfil. O perfil vem do `useAuth`
  // (derivado da sessão) e NÃO do `useCurrentAppUser` acima: a queryKey daquele
  // não tem o id do usuário e o cache não é limpo no logout (GUPM-241), então
  // após troca de usuário na mesma aba ele devolve o perfil do anterior — usá-lo
  // aqui replicaria o bug num gate novo. Mesma fonte que o `TarpDetailModal`.
  const { profile } = useAuth();
  const podeDarBaixa = canClose(profile);
  // GUPM-292: autoria de TODAS as TARPs da lista numa consulta (a função no banco
  // recebe uuid[]) — resolver card por card seria N+1 na tela mais aberta do app.
  // GUPM-301: inclui as ENCERRADAS. O momento `baixa` só existe depois do
  // encerramento — pedir autoria só das abertas garantia que "Baixa por" nunca
  // aparecesse. Limitado por construção: `listTarps` E `listBaixasEncerradas` cortam
  // em 200 cada (teto ~400 ids), e a função do banco recebe `uuid[]`, resolvendo tudo
  // numa consulta — não é N+1.
  //
  // CUSTO CONHECIDO (review do #181): `tarps` e `fechadas` vêm de duas queries
  // independentes que resolvem em momentos diferentes na rede real. A `queryKey` muda
  // quando a segunda chega, então há **uma RPC extra descartada** por montagem do M3.
  // Não é loop (a chave é ordenada e o hash é estável). Gatear `enabled` no
  // carregamento das duas resolveria, mas atrasaria o rótulo e criaria dependência do
  // estado de `useClosedTarps` — decisão deliberadamente deixada para card próprio.
  const { data: autoria = [] } = useTarpAutoria([...tarps, ...fechadas].map((t) => t.id));

  // Status consolidado por barragem (mesma fonte dos chips do header) → colore a pill.
  const damStatus = Object.fromEntries(damScope.map((d) => [d.code, d.status.nivel]));
  const { tarp, shiftHandover, planos } = useDataSources();
  const [pendingAction, setPendingAction] = useState<QuickAction | null>(null);
  const [resumingDraft, setResumingDraft] = useState<TarpRecord | null>(null);
  const [preselect, setPreselect] = useState<AbrirTarpPreselect | null>(null);
  // C-88 — motivo do atalho que não montou fluxo (antes o clique caía aqui em silêncio).
  const [avisoSemFluxo, setAvisoSemFluxo] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // GUPM-188: TARP fechada aberta no painel de auditoria (detalhe read-only).
  const [closedDetail, setClosedDetail] = useState<TarpRecord | null>(null);
  // GUPM-199: id de TARP a abrir via deep-link (Resumo de TARPs). Fica pendente até
  // a lista de abertas carregar (a resolução real acontece no efeito abaixo).
  const [pendingOpenId, setPendingOpenId] = useState<string | null>(null);
  const [openInBaixa, setOpenInBaixa] = useState(false);
  const [baixaError, setBaixaError] = useState<string | null>(null);
  // Baixa em envio (TOTP → plano → close): trava o modal do começo ao fim — C-91.
  const [baixando, setBaixando] = useState(false);
  // GUPM-213: plano criado na baixa, guardado para retry idempotente (se o `close`
  // falhar após criar o plano, a próxima tentativa reusa em vez de duplicar). Guarda
  // o `tarpId` para o cache valer SÓ para a mesma TARP — trocar de TARP após um close
  // falho não pode reusar o plano/resumo da anterior (review #124).
  const planoCriadoRef = useRef<{ tarpId: string; id: string; resumo: string } | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const selected = tarps.find((t) => t.id === selectedId) ?? null;
  // GUPM-213 Extra 2: planos já vinculados à TARP em detalhe → oferece "vincular existente" na baixa.
  const { data: planosVinculaveis = [], error: planosVinculaveisError } = usePlanosByTarp(selected?.id);
  // Observabilidade (frontend.md): a busca é fail-safe — na falha a baixa degrada para
  // "criar novo" —, mas o erro não pode passar calado (senão o RT não saberia que talvez
  // houvesse plano a vincular). Loga com contexto (só o UUID da TARP, sem PII). (review #124)
  useEffect(() => {
    if (planosVinculaveisError) {
      console.error('[TARP] falha ao carregar planos vinculáveis', {
        tarpId: selected?.id,
        error: planosVinculaveisError,
      });
    }
  }, [planosVinculaveisError, selected?.id]);

  // GUPM-185: auto-abertura por notificação de leitura anômala. O dashboard navega
  // pra cá com { tarpAutoOpen } no state do router (canal efêmero, não query string);
  // consumimos no mount, abrimos o formulário pré-preenchido com barragem+instrumento,
  // e LIMPAMOS o state (replace) para não reabrir em re-render / ao voltar.
  useEffect(() => {
    // GUPM-199: deep-link do Resumo de TARPs — abrir uma TARP existente. Guarda o id
    // e resolve quando a lista carregar (efeito abaixo); limpa o state do router pra
    // não reabrir no re-render / ao voltar. É a intenção "abrir existente" — distinta
    // dos acionamentos que criam TARP nova, por isso vem primeiro e retorna.
    const openId = readOpenTarpId(location.state);
    if (openId) {
      setAvisoSemFluxo(null);
      setPendingOpenId(openId);
      navigate('.', { replace: true, state: null });
      return;
    }
    // GUPM-190 T5: intenção de emergência (botão do header/dashboard) → abre a
    // TARP-EMG (acionamento manual, sem instrumento — emergência é da estrutura).
    if (readEmergencyIntent(location.state)) {
      setAvisoSemFluxo(null);
      setResumingDraft(null);
      setPreselect(null);
      setPendingAction(quickActionForCode('TARP-EMG'));
      navigate('.', { replace: true, state: null });
      return;
    }
    // GUPM-191 D2: acionamento manual vindo do dashboard → abre o fluxo daquele
    // código (mesmo efeito de clicar no card dentro do M3, sem fixar instrumento).
    const manual = readManualAction(location.state);
    if (manual) {
      const action = QUICK_ACTIONS.find((a) => a.code === manual);
      if (action) {
        setAvisoSemFluxo(null);
        setResumingDraft(null);
        setPreselect(null);
        setPendingAction(action);
      }
      navigate('.', { replace: true, state: null });
      return;
    }
    // C-88 — atalho sem fluxo: diz o motivo em vez de deixar o usuário na lista sem aviso.
    const semFluxo = readAtalhoSemFluxo(location.state);
    if (semFluxo) {
      setAvisoSemFluxo(mensagemSemFluxo(semFluxo));
      navigate('.', { replace: true, state: null });
      return;
    }
    const auto = readAutoOpen(location.state);
    if (!auto) return;
    setAvisoSemFluxo(null);
    setResumingDraft(null);
    setPreselect({
      damId: auto.damId,
      instrumentId: auto.instrumentId,
      instrumentCode: auto.instrumentCode,
      // GUPM-303 — o gatilho vem junto: a legenda do modo notificação precisa dizer se
      // barragem/instrumento vieram de uma leitura fora de faixa ou do silêncio.
      origem: auto.origem,
      // C-88 — o tipo dá nome ao título da TARP e adapta a releitura do tiltímetro.
      instrumentType: auto.instrumentType,
    });
    setPendingAction(quickActionForCode(auto.tarpCode));
    navigate('.', { replace: true, state: null });
  }, [location.state, navigate]);

  // GUPM-199: resolve o deep-link quando a lista de abertas carrega. Acha a TARP e
  // reusa o MESMO caminho do clique na lista (`handleSelect`): rascunho reabre o
  // editor; os demais abrem o detalhe read-only (Tela A). A baixa segue RT-only
  // (RLS + gating do modal) — o deep-link não cria caminho novo de escrita. Se o id
  // não estiver na lista (ex.: já encerrada), desiste em silêncio (não quebra).
  useEffect(() => {
    if (!pendingOpenId || isLoading) return;
    const alvo = tarps.find((t) => t.id === pendingOpenId);
    if (alvo) void handleSelect(alvo);
    setPendingOpenId(null);
    // handleSelect é recriado a cada render; depender dele re-dispararia o efeito. As
    // dependências reais são o id pendente e a lista (padrão de disable já usado no repo).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingOpenId, isLoading, tarps]);

  function handleAcionar(code: TarpCode) {
    const action = QUICK_ACTIONS.find((a) => a.code === code);
    if (action) {
      setAvisoSemFluxo(null);
      setResumingDraft(null);
      setPreselect(null); // acionamento manual não fixa instrumento (GUPM-174)
      setPendingAction(action);
    }
  }

  // Clicar num item da lista: rascunho EM_ABERTURA reabre o EDITOR (retomar de
  // onde parou); os demais estados abrem o detalhe.
  async function handleSelect(t: TarpRecord) {
    if (t.status === 'EM_ABERTURA') {
      // GUPM-300: `QUICK_ACTIONS` só tem os 5 acionamentos MANUAIS, mas o caminho
      // de notificação (GUPM-185/186, `quickActionForCode` logo acima) cria
      // rascunho de QUALQUER protocolo do catálogo. Sem o fallback, TARP
      // automática nascia inalcançável: caía no detalhe read-only e, como
      // "continuar" e "Descartar rascunho" moram no editor, ficava sem NENHUMA
      // ação possível — para nenhum perfil. Medido em produção: 4 de 4 rascunhos
      // vivos presos, o mais antigo há 15 dias, um de nível EMERGENCIA.
      // A guarda `isTarpCode` importa: código fora do catálogo cairia no default
      // de `quickActionForCode` (rótulo cru + nível ATENCAO) e abriria um editor
      // sem fluxo. Sem entrada no catálogo, segue para o detalhe.
      const action =
        QUICK_ACTIONS.find((a) => a.code === t.tarpCode) ??
        (isTarpCode(t.tarpCode) ? quickActionForCode(t.tarpCode) : undefined);
      if (action) {
        // O autosave não invalida a lista (evita refetch a cada tecla), então o
        // registro em cache pode estar defasado. Busca o rascunho MAIS RECENTE
        // para hidratar o checklist exatamente de onde parou; se a busca falhar,
        // cai no registro em cache (não trava a reabertura).
        let fresh = t;
        try {
          fresh = (await tarp.getTarp(t.id)) ?? t;
        } catch {
          /* usa o registro em cache */
        }
        setResumingDraft(fresh);
        setPreselect(null); // retomar rascunho não é modo notificação
        setPendingAction(action);
        return;
      }
    }
    // Corpo do card → revisão read-only (Tela A).
    openDetail(t, false);
  }

  // Abre o detalhe (modal). `inBaixa` decide a tela inicial: false = revisão
  // read-only (Tela A); true = formulário de baixa direto (Tela B). Limpa o erro e
  // reseta as mutations de página — elas persistiriam um erro antigo entre
  // reaberturas (o `key` remonta o modal, não as mutations).
  function openDetail(t: TarpRecord, inBaixa: boolean) {
    setBaixaError(null);
    conclude.reset();
    close.reset();
    setOpenInBaixa(inBaixa);
    setSelectedId(t.id);
  }

  // Atalho da lista: o botão "Dar Baixa" do card (só em CONCLUIDA_AGUARDANDO) pula
  // a revisão e abre direto a Tela B. A guarda de status do modal e a RLS RT-only
  // continuam sendo o gate real.
  function handleDarBaixa(t: TarpRecord) {
    openDetail(t, true);
  }

  function closeEditor() {
    setPendingAction(null);
    setResumingDraft(null);
    setPreselect(null);
  }

  async function handleConclude() {
    if (!selected) return;
    try {
      await conclude.mutateAsync(selected.id);
    } catch (e) {
      // conclude.error alimenta a prop `error` do modal; loga para observabilidade.
      console.error('[TARP] falha ao concluir/encaminhar', { tarpId: selected.id, error: e });
    }
  }

  async function handleBaixa(input: BaixaInput, totpCode: string) {
    if (!selected) return;
    setBaixaError(null);
    // C-91: o envio começa aqui, não no `closeTarp` — TOTP e plano vêm antes. O modal
    // trava (×/Esc/Concluir) durante TODO o envio, não só no `close.isPending`.
    setBaixando(true);
    try {
      await enviarBaixa(selected, input, totpCode);
    } finally {
      setBaixando(false);
    }
  }

  async function enviarBaixa(selected: TarpRecord, input: BaixaInput, totpCode: string) {
    // Fail-closed (self-review do #180), simétrico à guarda de sessão/TOTP abaixo: o
    // handler PERSISTE dados e montava o plano com `acoesValidas` — filtro, não `every`.
    // Hoje o botão desabilitado já barra este caminho; a guarda existe para que um
    // refactor do rodapé não reintroduza o descarte silencioso sem nenhum teste pegar.
    // Não é segunda fonte de verdade: chama a MESMA `validateBaixa` que a tela usa.
    const invalido = validateBaixa(input);
    if (invalido.length > 0) {
      setBaixaError(invalido.join(' '));
      return;
    }
    let totpVerified = false;
    try {
      // Autorização (security.md): TOTP verificado server-side. Em DEV, o PIN de
      // teste (isDevAuthBypass) destrava o fluxo sem o Edge verify-totp (503 local),
      // registrando a baixa como não-verificada. A RLS RT-only é o gate real.
      const code = totpCode.trim();
      if (!isDevAuthBypass(code, import.meta.env.DEV)) {
        const me = await shiftHandover.getCurrentUser();
        // Fail-closed: sem usuário resolvido não há como verificar o TOTP — não
        // segue para o encerramento (defesa em profundidade; a RLS RT-only ainda
        // gateia server-side, mas o cliente não deve pular a autorização calado).
        if (!me) {
          setBaixaError('Sessão expirada ou usuário não identificado. Faça login novamente.');
          return;
        }
        const res = await shiftHandover.verifyTotp(me.id, code, 'baixa_tarp');
        if (!res.success) {
          setBaixaError(baixaAuthErrorMessage(res.error, import.meta.env.DEV));
          return;
        }
        totpVerified = true;
      }
      // GUPM-213: cria o plano de ação estruturado ANTES de fechar (fail-safe — nunca
      // fecha TARP sem o plano quando há ações; um plano órfão é recuperável). A retry
      // após falha no `close` reusa o plano já criado (planoCriadoRef), sem duplicar.
      const acoesDoPlano = acoesValidas(input.acoes);
      // Cache do retry idempotente vale SÓ para a MESMA TARP (review #124): trocar de
      // TARP após um close falho não pode reusar o plano/resumo/deep-link da anterior.
      const cache = planoCriadoRef.current?.tarpId === selected.id ? planoCriadoRef.current : null;
      let planoId: string | null = cache?.id ?? null;
      let planoResumo = cache?.resumo ?? '';
      if (input.planoExistenteId) {
        // Extra 2: o RT vinculou um plano JÁ existente da TARP — não cria outro.
        const existente = planosVinculaveis.find((p) => p.id === input.planoExistenteId);
        planoId = input.planoExistenteId;
        // Resumo puro/testável; fallback não-vazio quando o plano não está no cache/lista (review #124).
        planoResumo = resumoPlanoExistente(existente);
      } else if (input.planoAplica && acoesDoPlano.length > 0 && !cache) {
        const novoPlano = await planos.createPlano({
          titulo: `Plano de ação — ${selected.tarpCode}`,
          tarpRecordId: selected.id,
          tarpCode: selected.tarpCode,
          damId: selected.damId,
          nivel: input.enquadramento ? NE_TO_TARP_LEVEL[input.enquadramento] : null,
          modo: `Ações definidas na baixa da ${selected.tarpCode}`,
          instrumento: selected.instrumentCode ?? null,
          acoes: acoesDoPlano.map(draftToAcaoPlano),
        });
        planoId = novoPlano.id;
        planoResumo = resumoPlano(input.acoes, novoPlano.codigo);
        planoCriadoRef.current = { tarpId: selected.id, id: planoId, resumo: planoResumo };
      }
      await close.mutateAsync({
        id: selected.id,
        input: {
          parecer: input.parecer.trim(),
          enquadramento: input.enquadramento,
          planoAcao: planoResumo,
          totpVerified,
        },
      });
      planoCriadoRef.current = null; // baixa concluída — libera o ref para a próxima
      setSelectedId(null); // encerrada sai da lista de abertos
      // GUPM-213: leva ao módulo de Planos de Ação com o plano recém-criado em foco.
      if (planoId) navigate('/planos', { state: openPlanoNavState(planoId) });
    } catch (e) {
      // NÃO engolir: um throw ANTES do close (ex.: getCurrentUser sob sessão
      // expirada) deixaria o RT sem feedback, achando que a baixa ocorreu.
      console.error('[TARP] falha ao dar baixa', { tarpId: selected.id, error: e });
      setBaixaError(errMsg(e) ?? 'Falha ao registrar a baixa. Tente novamente.');
    }
  }

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-2xl font-black text-jf-yellow">🛡️ TARP — Plano de Resposta</h1>
        <p className="mt-1 text-sm text-jf-muted">
          Registro auditável e escopado por barragem/instrumento (v5.10).
        </p>
      </header>

      <BannerArea modulo="M3" />

      {avisoSemFluxo && <AvisoSemFluxo mensagem={avisoSemFluxo} onFechar={() => setAvisoSemFluxo(null)} />}

      {/* Entrada manual fiel ao protótipo — reutilizável no dashboard depois. */}
      <AcionamentosManuais onAcionar={handleAcionar} />

      <section className="rounded-xl border border-jf-border bg-jf-surface p-4">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-bold text-white">TARPs em aberto</h2>
          {tarps.length > 0 && (
            <span className="rounded-full border border-level-alerta/40 bg-level-alerta/15 px-2.5 py-0.5 text-[11px] font-bold text-level-alerta">
              {tarps.length}
            </span>
          )}
        </div>

        {isLoading && <p className="py-6 text-center text-xs text-jf-muted">Carregando…</p>}
        {listError && (
          <p className="py-6 text-center text-xs text-level-emergencia">
            Falha ao carregar TARPs: {errMsg(listError)}
          </p>
        )}
        {!isLoading && !listError && (
          <TarpList
            tarps={tarps}
            onSelect={(t) => void handleSelect(t)}
            onDarBaixa={handleDarBaixa}
            damStatus={damStatus}
            podeDarBaixa={podeDarBaixa}
            autoria={autoria}
          />
        )}
      </section>

      {/* GUPM-188: auditoria — TARPs fechadas (colapsável, filtros, ordenação) no rodapé. */}
      <TarpsFechadasPanel tarps={fechadas} onSelect={setClosedDetail} autoria={autoria} />

      {pendingAction && (
        <AbrirTarpFlow
          key={resumingDraft?.id ?? preselect?.instrumentId ?? `new-${pendingAction.code}`}
          action={pendingAction}
          dams={dams}
          existingDraft={resumingDraft}
          preselect={preselect}
          canDiscard={
            resumingDraft
              ? resumingDraft.openedById === currentUser?.id ||
                currentUser?.profile === 'tecnico_responsavel' ||
                currentUser?.profile === 'admin'
              : true
          }
          onClose={closeEditor}
        />
      )}

      {selected && (
        <TarpDetailModal
          key={selected.id}
          tarp={selected}
          planosVinculaveis={planosVinculaveis}
          // GUPM-190 T7 (emenda ao GUPM-184, ver ADR-0021): clicar no CORPO do card
          // abre a Tela A read-only; só o atalho "Dar Baixa" do card (openInBaixa)
          // abre direto a Tela B. Default read-only.
          iniciarRegistrando={openInBaixa}
          onClose={() => {
            setSelectedId(null);
            setOpenInBaixa(false);
          }}
          onConclude={handleConclude}
          onBaixa={handleBaixa}
          concluding={conclude.isPending}
          closing={baixando || close.isPending}
          error={baixaError ?? errMsg(conclude.error) ?? errMsg(close.error)}
        />
      )}

      {/* GUPM-188: detalhe read-only de uma TARP fechada (ENCERRADA) — sem baixa. */}
      {closedDetail && (
        <TarpDetailModal
          key={closedDetail.id}
          tarp={closedDetail}
          onClose={() => setClosedDetail(null)}
          onConclude={() => {}}
          onBaixa={() => {}}
          concluding={false}
          closing={false}
          error={null}
        />
      )}
    </div>
  );
}
